import React, { ReactNode, useState, memo, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  CssBaseline,
  Divider,
  IconButton,
  List,
  ThemeProvider,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  createTheme,
  Tooltip,
} from '@mui/material';

import { SnackbarProvider } from 'notistack';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';

import Sidebar from './SideBar';
import BottomMenu from './BottomMenu';
import AppBar from './AppBar';
import Drawer from './Drawer';
import Loading from '../Loading';
import { useLocation } from 'react-router-dom';
import { authData } from '../../Storages';
import getActiveModules from '../../Storages/getActiveModules';

import { useThemeMode } from '../../ThemeContext';
import '../../Css/Layout/LayoutConstants.css';

const getModuloName = (pathname: string): string => {
  const accessProfileModules = getActiveModules();
  const path = pathname.split('/').filter(Boolean).pop() || '';
  const found = accessProfileModules?.find((m: { route_name: string }) => m.route_name === `/${path}`);
  return found?.description || '';
};

type Props = {
  children?: ReactNode;
};

const MainLayout: React.FC<Props> = ({ children }) => {
  const { toggleColorMode, mode } = useThemeMode();

  const muiTheme = useMemo(() =>
    createTheme({
      palette: {
        mode,
        primary: {
          main: '#1c2e4a',
        },
        background: {
          default: mode === 'dark' ? '#121212' : '#f3f4f8',
        },
      },
      // Definindo os espaçamentos padrão do tema para alinhar com nossas variáveis CSS
      spacing: (factor: number) => `${8 * factor}px`,
      components: {
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 'var(--border-radius-md)',
              boxShadow: 'var(--shadow-card)',
            }
          }
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              padding: 'var(--spacing-md)',
              '&:last-child': {
                paddingBottom: 'var(--spacing-md)',
              }
            }
          }
        },
        MuiGrid: {
          styleOverrides: {
            container: {
              marginTop: '0',
              marginBottom: 'var(--spacing-sm)'
            },
            item: {
              padding: 'var(--spacing-sm)'
            }
          }
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              padding: 'var(--spacing-sm) var(--spacing-md)'
            }
          }
        }
      }
    }),
    [mode]
  );

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const location = useLocation();
  const [routeName, setRouteName] = useState('');
  const [isSidebarVisible, setIsSidebarVisible] = useState(!isMobile);
  const [open, setOpen] = useState(!isMobile);

  const { name: fullName = '' } = authData.get();
  const userName = fullName.slice(0, 15);

  const toggleDrawer = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  useEffect(() => {
    setRouteName(getModuloName(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    setIsSidebarVisible(!isMobile);
    setOpen(!isMobile);
  }, [isMobile]);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <SnackbarProvider maxSnack={3}>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <AppBar open={open} toggleDarkMode={toggleColorMode} isDarkMode={mode === 'dark'}>
            <Toolbar sx={{ paddingRight: 'var(--spacing-md)' }}>
              {isSidebarVisible && (
                <IconButton
                  edge="start"
                  color="inherit"
                  aria-label="open drawer"
                  onClick={toggleDrawer}
                  sx={{ marginRight: 'var(--spacing-lg)', ...(open && { display: 'none' }) }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
                {routeName}
              </Typography>
            </Toolbar>
          </AppBar>

          {isSidebarVisible && (
            <Drawer
              variant="permanent"
              open={open}
              sx={{
                '& .MuiDrawer-paper': {
                  backgroundColor: '#1c2e4a',
                  color: '#ffffff',
                  width: open ? 240 : muiTheme.spacing(7),
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: 0,
                },
              }}
            >
              <Toolbar
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  backgroundColor: '#1c2e4a',
                  color: '#ffffff',
                  padding: 'var(--spacing-md)',
                }}
              >
                Olá, {userName}
                <IconButton onClick={toggleDrawer}>
                  <ChevronLeftIcon sx={{ color: '#ffffff' }} />
                </IconButton>
              </Toolbar>
              <List component="nav">
                <Sidebar />
              </List>
            </Drawer>
          )}

          <Box
            component="main"
            sx={{
              backgroundColor: muiTheme.palette.background.default,
              flexGrow: 1,
              minHeight: '100vh',
              overflow: 'auto',
            }}
          >
            <Toolbar />
            <Container 
              maxWidth={false}
              sx={{ 
                marginTop: 'var(--spacing-md)',
                marginBottom: 'var(--spacing-lg)',
                paddingBottom: 'var(--spacing-lg)',
                width: 'var(--container-max-width)',
                marginLeft: 'auto',
                marginRight: 'auto'
              }}
            >
              {children}
            </Container>
          </Box>
        </Box>

        {isMobile && <BottomMenu />}
        <Loading />
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default memo(MainLayout);
